import React from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { MdModeEdit, MdAdd, MdClose } from 'react-icons/md';

import Alert from 'components/common/alert';

import { DATE_FORMAT_FOR_DISPLAY } from 'constants/appConstants';
import type { LeapfrogHistory } from 'interface/createUpdateForm';

type EngagementHistoryEntry = LeapfrogHistory['empStatusHistory'][number] & {
  uid?: string;
};

interface EngagementStatusHistoryProps {
  isCreateForm: boolean;
  selectedEmployeeStatus: EngagementHistoryEntry | null;
  history: EngagementHistoryEntry[];
  addEngagementStatusHistory: () => void;
  onUpdate: (status: EngagementHistoryEntry) => void;
  onDelete: (status: EngagementHistoryEntry) => void;
  disabled?: boolean;
}

const EngagementStatusHistory: React.FC<EngagementStatusHistoryProps> = (props) => {
  const {
    isCreateForm,
    selectedEmployeeStatus,
    history: empStatusHistory,
    addEngagementStatusHistory,
    onUpdate: handleEngagementStatusUpdateClick,
    onDelete: handleRemoveEngagementStatusHistory,
    disabled,
  } = props;

  const removeEngagementStatusHistory = (empEngagement: EngagementHistoryEntry) => {
    Alert.fire({
      icon: 'warning',
      title: 'Remove this Employment Status History',
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',

      preConfirm: () => {
        try {
          handleRemoveEngagementStatusHistory(empEngagement);
        } catch (err) {
          Alert.showValidationMessage(`Request failed: ${String(err)}`);
        }
      },
    });
  };

  const employmentHistory = empStatusHistory.map((empEngagement, index) => {
    if (!empEngagement?.transitionDate) {
      return <React.Fragment key={empEngagement?.id || empEngagement?.uid || index} />;
    }
    const isLastElement = empStatusHistory.length - 1 === index;

    return (
      <div
        className="position-relative d-inline-block text-align--center employee-history-container"
        key={empEngagement.id ? empEngagement.id : empEngagement.uid}
      >
        <div
          className={classnames('employee-history mt-25 mb-10', {
            'employee-history-last': isLastElement,
          })}
        >
          <div className="capital-text employee-history-title">{empEngagement.engagementStatus.name}</div>
          <div className="employee-history-detail">
            {moment(empEngagement.transitionDate).format(DATE_FORMAT_FOR_DISPLAY)}
          </div>
          <button
            type="button"
            disabled={disabled}
            className={classnames('d-flex align-items-center edit-button mt-10 m-auto', {
              'active-edit': empEngagement && empEngagement === selectedEmployeeStatus,
            })}
            onClick={() => handleEngagementStatusUpdateClick(empEngagement)}
          >
            <MdModeEdit />
            &nbsp;Edit
          </button>

          <button
            type="button"
            disabled={empStatusHistory.length === 1 || disabled}
            className="d-flex align-items-center mt-10 m-auto remove-button"
            onClick={() => removeEngagementStatusHistory(empEngagement)}
          >
            <MdClose />
            &nbsp;Remove
          </button>
        </div>
      </div>
    );
  });

  return (
    <div className="d-flex mb-30 mt-30 overflow-flex custom-scroll-bar">
      {employmentHistory}

      {!isCreateForm && (
        <div className="d-inline-block employee-history-container">
          <button type="button" onClick={addEngagementStatusHistory} disabled={disabled} className="icon-round m-auto">
            <MdAdd size="30" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EngagementStatusHistory;
